/** venture/costep.ts — 8단계부터 법인별 독립 진행 (진행상태 이원화, 2026-07-19)
 *  1~7단계: 조합 공통 진행(p.step). 8~LAST(=STEPS.length, 2026-09-24 현재 15)단계: 법인별 커서 costep[coId].
 *  p.step은 8+ 구간에서 min(costep)로 파생 → 타임라인·StepBody·네비게이션 무수정 동작.
 *  costep 미존재 시 p.step 폴백 → 기존 프로젝트 데이터 마이그레이션 불필요. */
import { STEPS } from './types';
import type { Project, CoCard } from './types';

export const SPLIT_FROM = 8;
export const LAST = STEPS.length;
export type Stat = 'wait' | 'cur' | 'done';

type StepMap = Record<string, number>;
type DoneMap = Record<string, boolean>;

// 8단계를 아직 시작 안 한 법인을 '대기'로 표현하려면 커서가 8보다 한 칸 아래여야 한다.
const WAIT_AT_FIRST = SPLIT_FROM - 1;
const clamp = (n: number): number => Math.min(LAST, Math.max(WAIT_AT_FIRST, n));
export const cosOf = (p: Project): CoCard[] => (p.collect && p.collect.cos) || [];
export const isSplit = (n: number): boolean => n >= SPLIT_FROM && n <= LAST;

export function coStep(p: Project, coId: string): number {
  const m = p.costep as StepMap | undefined;
  const v = m && typeof m[coId] === 'number' ? m[coId] : p.step;
  return clamp(v);
}

export function coDone(p: Project, coId: string): boolean {
  const m = p.codone as DoneMap | undefined;
  if (m && typeof m[coId] === 'boolean') return m[coId];
  return !!p.done;
}

export function coStat(p: Project, coId: string, n: number): Stat {
  if (coDone(p, coId)) return 'done';
  const c = coStep(p, coId);
  if (n < c) return 'done';
  return n === c ? 'cur' : 'wait';
}

/** 법인별 커서에서 프로젝트 커서(p.step·p.done)를 파생 — 가장 늦은 법인이 프로젝트의 현재 단계 */
function derive(p: Project, step: StepMap, done: DoneMap): Partial<Project> {
  const cos = cosOf(p);
  if (!cos.length) return {};
  const allDone = cos.every((co) => !!done[co.id]);
  if (allDone) return { step: LAST, done: true };
  let min = LAST;
  for (let i = 0; i < cos.length; i++) {
    const id = cos[i].id;
    if (done[id]) continue;
    const v = clamp(step[id]);
    if (v < min) min = v;
  }
  // 전 법인이 8단계 대기여도 프로젝트는 8단계에 머문다.
  return { step: Math.max(SPLIT_FROM, min), done: false };
}

/** 현재 저장값을 법인 전원분으로 채운 맵 (부분 저장 상태에서도 파생이 안전하도록) */
function fill(p: Project): { step: StepMap; done: DoneMap } {
  const step: StepMap = { ...((p.costep as StepMap) || {}) };
  const done: DoneMap = { ...((p.codone as DoneMap) || {}) };
  cosOf(p).forEach((co) => {
    if (typeof step[co.id] !== 'number') step[co.id] = coStep(p, co.id);
    if (typeof done[co.id] !== 'boolean') done[co.id] = coDone(p, co.id);
  });
  return { step, done };
}

function apply(step: StepMap, done: DoneMap, coId: string, n: number, s: Stat): void {
  if (s === 'done') {
    if (n >= LAST) { step[coId] = LAST; done[coId] = true; }
    else { step[coId] = clamp(n + 1); done[coId] = false; }
  } else if (s === 'cur') {
    step[coId] = clamp(n); done[coId] = false;
  } else {
    step[coId] = clamp(n - 1); done[coId] = false;
  }
}

/** 법인 1곳의 단계 상태 변경 */
export function setCoStat(p: Project, coId: string, n: number, s: Stat): Partial<Project> {
  const { step, done } = fill(p);
  apply(step, done, coId, n, s);
  return { costep: step, codone: done, ...derive(p, step, done) };
}

/** 전 법인 일괄 적용 (타임라인 단계 버튼) */
export function setAllStat(p: Project, n: number, s: Stat): Partial<Project> {
  const { step, done } = fill(p);
  cosOf(p).forEach((co) => apply(step, done, co.id, n, s));
  return { costep: step, codone: done, ...derive(p, step, done) };
}

export interface Rollup { done: number; cur: number; wait: number; total: number }

export function rollup(p: Project, n: number): Rollup {
  const r: Rollup = { done: 0, cur: 0, wait: 0, total: 0 };
  cosOf(p).forEach((co) => { r[coStat(p, co.id, n)] += 1; r.total += 1; });
  return r;
}

/** 열람 가능 상한 — 가장 앞선 법인 기준 (선행 법인의 다음 단계도 열람 가능해야 함) */
export function maxStep(p: Project): number {
  // 8단계 전(조합 공통 구간)에는 법인별 커서가 아직 없다 — 예전엔 항상 ≥8을 돌려줘
  // 2단계 프로젝트에도 3~8단계에 「클릭하여 열기」가 떴지만 열리지는 않았다.
  if (p.step < SPLIT_FROM) return p.step;
  const cos = cosOf(p);
  let mx = p.step;
  for (let i = 0; i < cos.length; i++) {
    const id = cos[i].id;
    const v = coDone(p, id) ? LAST : coStep(p, id);
    if (v > mx) mx = v;
  }
  return Math.min(LAST, Math.max(SPLIT_FROM, mx));
}

/** 법인별 진행이 실제로 갈라져 있는지 (일괄 버튼 라벨 전환용) */
export function isDiverged(p: Project, n: number): boolean {
  const r = rollup(p, n);
  return r.total > 1 && [r.done, r.cur, r.wait].filter((v) => v > 0).length > 1;
}
